<template>
  <el-container class="subproperty-dashboard-finantial app-content-page">
    <div class="subproperty-dashboard-finantial__header">
        <!-- Title -->
        <span class="subproperty-dashboard-finantial__header__title">
          <app-i18n code="entities.subproperty.finantialOverview.title" />
        </span>

        <!-- Select --> 
        <app-subproperty-select
          v-if="showSelect"
          class="subproperty-dashboard-finantial__header__select"
          :subproperties="subproperties"
          :subproperty-id.sync="currentSubpropertyId"
        />
    </div>
    
    <el-row
      class="subproperty-dashboard-finantial__container"
      justify="center"
      v-loading="loading"
    >
      <el-col
        v-for="({ responsive, iconClass, iconStyle, style, textLayout, textBoxLayout, linkLayout, linkBoxLayout, statusLayout, buttonLayout, emptyLayout, errorLayout, infoLayout }, i) in finantialCardsFiltered"
        :key="`subproperty-dashboard-finantial__container--${i}`"
        :lg="responsive && responsive.lg ? responsive.lg : 24"
        :md="responsive && responsive.md ? responsive.md : 24"
        :sm="responsive && responsive.sm ? responsive.sm : 24"
      >
        <!-- Card -->
        <el-card
          class="subproperty-dashboard-finantial__container__card"
          :class="{ 'subproperty-dashboard-finantial__container__card--error': errorLayout }"
          :body-style="style"
        >
          <!-- Icon -->
          <i
            :class="iconClass"
            :style="iconStyle"
            class="subproperty-dashboard-finantial__container__card__icon"
          />
          <!-- Text -->
          <div
            v-if="textLayout"
            class="subproperty-dashboard-finantial__container__card__text"
            :style="textLayout.style"
          >
            <span>{{ textLayout.contentText }}</span>
          </div>

          <!-- Text Box-->
          <div
            v-if="textBoxLayout && textBoxLayout.params"
            :style="textBoxLayout.style"
            class="subproperty-dashboard-finantial__container__card__text-box"
          >
            <subprogram-payment-input-info-base
              class="subproperty-dashboard-finantial__container__card__text-box__content"
              v-for="(fee, i) in parseFees(textBoxLayout.params.fees)"
              :key="`total-fees--${i}`"
              :currency="textBoxLayout.params.currency"
              :amount="fee.amount"
              :percent="fee.percentage"
              :icon-class="fee.iconClass"
              :info="fee.info"
            />
          </div>

          <!-- Link -->
          <div
            v-if="linkLayout"
            :style="linkLayout.style"
            class="subproperty-dashboard-finantial__container__card__link"
          >
            <a 
              v-if="linkLayout.linkUrl"
              :href="linkLayout.linkUrl"
              target="_blank"
              v-text="linkLayout.linkUrl"
            />
            <router-link 
              v-else
              :to="linkLayout.linkTo"
            >
              <span v-text="linkLayout.contentText" />
            </router-link>
          </div>

          <!-- Link box-->
          <div
            v-if="linkBoxLayout"
            :style="linkBoxLayout.style"
            class="subproperty-dashboard-finantial__container__card__link-box"
          >
            <a 
              v-if="linkBoxLayout.linkUrl"
              :href="linkBoxLayout.linkUrl"
              target="_blank"
              v-text="linkBoxLayout.linkUrl"
            />
            <router-link 
              v-else
              :to="linkBoxLayout.linkTo"
            >
              <span v-text="linkBoxLayout.contentText" />
            </router-link>
          </div>

          <!-- Status -->
          <app-status
            v-if="statusLayout"             
            class="subproperty-dashboard-finantial__container__card__status"
            :enabled="statusLayout.enabled"
            :style="statusLayout.style"
            :color="statusLayout.iconColor"
            :text="statusLayout.contentText"
          />

          <!-- Empty caption -->
          <div
            v-if="emptyLayout"
            :style="emptyLayout.style"
            class="subproperty-dashboard-finantial__container__card__empty"
          >
            <p
              class="subproperty-dashboard-finantial__card__title"
              v-text="emptyLayout.contentText"
            />
          </div>
        </el-card>

        <!-- Error -->
        <div
          v-if="errorLayout"
          class="subproperty-dashboard-finantial__container__error"
        >
          <i
            class="subproperty-dashboard-finantial__container__notification__error__icon icon-warning"
          />
          <span
            class="subproperty-dashboard-finantial__container__notification__error__text"
            v-text="errorLayout.contentText"
          />
        </div>

        <!-- Info -->
        <div
          v-if="infoLayout && currentManage.type === 'subproperty'"
          class="subproperty-dashboard-finantial__container__info"
        >
          <i class="subproperty-dashboard-finantial__container__info__icon icon-info" />
          <span
            class="subproperty-dashboard-finantial__container__info__text"
            v-text="infoLayout.contentText"
          />
        </div>
      </el-col>
    </el-row>
  </el-container>
</template>

<script>
import { get } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import { i18n } from '@shared/i18n';

import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';

import SubpropertyListFilter from '@modules/subproperty/components/subproperty-list-filter.vue';
import SubpropertyListTable from '@modules/subproperty/components/subproperty-list-table.vue';
import SubpropertySelect from '@modules/subproperty/components/subproperty-select.vue';

import SubprogramPaymentInputInfoBase from '@modules/subprogram/components/subprogram-payment-input-info-base';

const configDefaultFee = {
  label: `${i18n('entities.subprogram.form.payment.fee.label')}`,
  iconClass: 'icon-tax'
}

const ConfigFeesInfo =  {
  EUROPEAN: {
    ...configDefaultFee,
    info: `${i18n('entities.subprogram.form.payment.fee.forEuropean')}`,
  },
  NON_EUROPEAN:{
    ...configDefaultFee,
    info: `${i18n('entities.subprogram.form.payment.fee.forNonEuropean')}`,
  },
  COUNTRY: {
    ...configDefaultFee,
    info: `${i18n('entities.subprogram.form.payment.fee.forCountry')}`,
  },
  INTERNATIONAL: {
    ...configDefaultFee,
    info: `${i18n('entities.subprogram.form.payment.fee.forInternational')}`,
  }
}

export default {
  name: 'subproperty-dashboard-finantial',

  props: {
    subproperties: {
      type: Array,
    },
    subpropertyId: {
      type: String,
    },
    hiddenLinks: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      tooltipIcon: faQuestionCircle,
      currentSubpropertyId: null,
      currentSubpropertyPaymentsEnabled: null,
      currentSubpropertyCountry: null,
    };
  },

  computed: {
    ...mapGetters({
      currentManage: 'auth/manage/currentManage',
      disabled: 'subproperty/dashboardFinantial/disabled',
      rows: 'subproperty/list/rows',
      loading: 'subproperty/dashboardFinantial/loading',
      finantialCardsObject: 'subproperty/dashboardFinantial/finantialCardsObject',
      hasOwqloFeeConfigured: 'subproperty/dashboardFinantial/hasOwqloFeeConfigured',
    }),

    finantialCardsFiltered() {
      const cardsObject = Object.assign({}, this.finantialCardsObject);
      const paymentsEnabled = this.hasOwqloFeeConfigured;

      if (!paymentsEnabled) {
        delete cardsObject.fee;
        delete cardsObject.connectedAccount;
      }

      return Object.values(cardsObject);
    },

    propertyId() {
      return get(this.currentManage, 'property.id', null);
    },

    showSelect() {
      return Array.isArray(this.subproperties) && this.subproperties.length > 0 && this.currentSubpropertyId;
    },
  },

  async mounted() {
    if (this.$route.name !== this.currentManage.type) return
    switch (this.currentManage.type) {
      case 'property':
        this.currentSubpropertyId = this.subproperties[0].id;
        break;
      case 'subproperty':
        this.currentSubpropertyId = this.currentManage.subproperty.id;
        break;
    }
  },

  methods: {
    ...mapActions('subproperty/dashboardFinantial', [
      'doFetch',
      'doConnectedAccountCardError',
      'doConnectedAccountCardErrorRemove',
      'doRemoveLinks',
      'doReset',
    ]),

    async getCurrentSubpropertyData(subpropertyId) {
      const subproperties = this.subproperties ? this.subproperties.slice(0) : [];

      if (Array.isArray(subproperties) && subproperties.length > 0) {
        const i = subpropertyId ? subproperties.findIndex(s => s.id === subpropertyId) : 0;
        const paymentsEnabled = get(this, `subproperties[${i}].paymentsEnabled`, null);

        this.currentSubpropertyPaymentsEnabled = paymentsEnabled;
        this.currentSubpropertyId = get(this, `subproperties[${i}].id`, null);
        this.currentSubpropertyCountry = get(this, `subproperties[${i}].country`, null);
      } else {
        const paymentsEnabled = get(this, 'currentManage.subproperty.paymentsEnabled', null);

        this.currentSubpropertyPaymentsEnabled = paymentsEnabled;
        this.currentSubpropertyId = get(this, 'currentManage.subproperty.id', null);
        this.currentSubpropertyCountry = get(this, 'currentManage.subproperty.country', null);
      }

      if (!subpropertyId) return;

      await this.doFetch({
        subpropertyId: this.currentSubpropertyId,
        propertyId: this.propertyId,
      });

      if (this.hiddenLinks) this.doRemoveLinks();
    },

    parseFees(fees) {
      const { currentSubpropertyCountry: country } = this
      if (!country) return []

      return fees.map(({ type, amount, percentage }) => ({
        ...ConfigFeesInfo[type],
        type,
        percentage,
        amount: amount / 100,
      }))
    },
  },

  watch: {
    async currentSubpropertyId (id, oldId) {
      if (id === oldId) return;
      this.getCurrentSubpropertyData(id);
    },
  },

  components: {
    [SubpropertyListTable.name]: SubpropertyListTable,
    [SubpropertyListFilter.name]: SubpropertyListFilter,
    [SubpropertySelect.name]: SubpropertySelect,
    [SubprogramPaymentInputInfoBase.name]: SubprogramPaymentInputInfoBase,
  },
};
</script>

<style lang="scss" scoped>
.subproperty-dashboard-finantial {
  padding: 0;
  flex-direction: column;

  p {
    margin: 0;
  }

  &__header {
    color: #666666;
    border-bottom: 1px solid #cccccc;
    padding: 30px 54px 22px;

    display: grid;
    grid-template-columns: minmax(auto, 167px) auto;
    align-items: center;

    &__title {
      font-size: 14px;
      letter-spacing: 0.2px;
    }
  }

  &__container {
    padding: 30px 30px 22px 40px;

    &__card {
      grid-area: card;
      border: 0;
      border-radius: 0;
      box-shadow: -10px 10px 20px 0 rgba(30, 30, 30, 0.05);
      margin: 12px;
      padding: 10px 24px;
      align-items: center;

      &__status {
        padding: 8px 16px;
      }

      &__icon {
        color: #c5c5c5;
        font-size: 24px;
      }

      &__text {
        color: #5d5d5d;
        font-size: 16px;
        align-self: end;

        &-box {
          display: grid;
          grid-template-columns: auto auto;
          grid-gap: 16px;

          &__content {
            box-shadow: 0 2px 5px 0 rgba(223, 223, 223, 0.7);
            border-radius: 8px;
            padding: 8px 16px;

            &:first-of-type {
              padding: 8px 16px;
            }
          }
        }
      }
    
      &__link {
        color: #2acccc;
        line-height: 1;
        font-weight: bold;
        margin-top: 4px;
        align-self: start;

        &-box {
          display: flex;
          justify-content: flex-end;

          a {
            color: #2acccc;
            text-align: center;
            font-weight: bold;
            box-shadow: 0 2px 5px 0 rgba(223, 223, 223, 0.7);
            border-radius: 8px;
            padding: 8px 16px;
            max-width: 100%;
            width: auto;
            display: inline-block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            box-sizing: border-box;
          }
        }
      }

      &__empty {
        font-size: 12px;
        line-height: 1.42;
        letter-spacing: -0.3px;
        color: #c5c5c5;
        align-self: center;
      }

      &--error {
        box-shadow: -2px 0 12px 0 rgba(239, 0, 0, 0.16);
        border-radius: 4px;
      }
    }

    &__error {
      grid-area: error;
      color: #ee7266;
      padding-left: 15px;
      font-size: 12px;

      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 18px auto;
      grid-template-areas: 'icon_error text_error';
      align-items: center;

      &__icon {
        grid-area: icon_error;
      }
      &__text {
        grid-area: text_error;
        color: #606266;
      }
    }

    &__info {
      grid-area: info;
      padding-left: 15px;
      font-size: 12px;

      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 18px auto;
      grid-template-areas: 'icon_info text_info';
      align-items: center;

      &__icon {
        color: #c5c5c5;
        grid-area: icon_info;
      }
      &__text {
        grid-area: text_info;
        color: #606266;
      }
    }
  }
}
</style>
